/**
 * Ground Tab Content
 * Surface library for selecting ground textures.
 * Drawing tools are in the PlacementPanel (UX16).
 */

import './GroundTabContent.css'
import type { TabContentRenderer } from './LeftPanel'
import { LAYER_CONFIG } from './LeftPanel'
import { ProductBrowser } from './ProductBrowser'
import { startWalkthrough } from './Walkthrough'
import { t } from '@/i18n'

export class GroundTabContent implements TabContentRenderer {
  private container: HTMLElement | null = null
  private productBrowser: ProductBrowser

  constructor() {
    this.productBrowser = new ProductBrowser({
      layer: 'ground',
      accentColor: LAYER_CONFIG.ground.color,
      showSearch: true,
      searchPlaceholder: t('ground.searchPlaceholder', 'Search surfaces...'),
      onProductSelected: () => {
        this.updateHint()
      },
    })
  }

  render(container: HTMLElement): void {
    this.container = container
    this.renderContent()
  }

  destroy(): void {
    this.productBrowser.destroy()
    this.container = null
  }

  onActivate(): void {
    this.updateHint()
  }

  onDeactivate(): void {
    // nothing
  }

  private renderContent(): void {
    if (!this.container) return

    this.container.innerHTML = `
      <div class="ground-tab-content">
        <!-- Surface Library -->
        <div class="ground-section-title">
          <span>${t('ground.surfaceLibrary', 'Surface Library')}</span>
          <button class="tab-help-btn" data-walkthrough="ground" title="${t('tooltip.help', 'Help')}">
            <i class="fa-solid fa-circle-question"></i>
          </button>
        </div>
        <div class="ground-product-browser"></div>

        <!-- Hint Bar -->
        <div class="ground-hint-bar">
          <i class="fa-solid fa-circle-info"></i>
          <span class="ground-hint-text">${t('ground.hintDefault', 'Select a surface, then draw on canvas')}</span>
        </div>
      </div>
    `

    // Render product browser into its container
    const browserContainer = this.container.querySelector('.ground-product-browser') as HTMLElement
    if (browserContainer) {
      this.productBrowser.render(browserContainer)
    }

    // Wire help button
    this.container.querySelector('.tab-help-btn')?.addEventListener('click', () => startWalkthrough('ground'))
  }

  private updateHint(): void {
    if (!this.container) return
    const hintEl = this.container.querySelector('.ground-hint-text')
    if (hintEl) {
      hintEl.textContent = t('ground.hintDefault', 'Select a surface, then draw on canvas')
    }
  }
}
